import React, { useState } from "react";
import { ChevronRight, ChevronLeft, Mic, CheckCircle, AlertCircle, Users, Code2, MessageSquare, Trophy, Camera, CameraOff } from "lucide-react";


const INTERVIEW_TYPES = [
  { key: "Triagem",        icon: Users,         color: "#00664C", bg: "#EAFAF6", desc: "15 min · Fit cultural e perguntas gerais" },
  { key: "Técnica",        icon: Code2,         color: "#003326", bg: "#B0FCEA", desc: "20–30 min · Cases, ferramentas e decisões" },
  { key: "Comportamental", icon: MessageSquare, color: "#65541B", bg: "#FEEDB4", desc: "20 min · Situações e repertório" },
  { key: "Finalista",      icon: Trophy,        color: "#892E38", bg: "#FADBDF", desc: "30 min · Pressão máxima e profundidade" },
];

export const INTERVIEWER_PROFILES = [
  {
    key: "Gestor exigente",
    name: "Ricardo",
    avatarUrl: "https://api.dicebear.com/9.x/micah/svg?seed=Ricardo&baseColor=f5cfa0&earringsProbability=0",
    desc: "Objetivo e direto. Vai no ponto sem rodeios.",
    voiceId: "pNInz6obpgDQGcFmaJgB",
  },
  {
    key: "Recrutadora",
    name: "Camila",
    avatarUrl: "https://api.dicebear.com/9.x/lorelei/svg?seed=Camila",
    desc: "Comportamental. Adora explorar situações passadas.",
    voiceId: "21m00Tcm4TlvDq8ikWAM",
  },
  {
    key: "Founder",
    name: "Lucas",
    avatarUrl: "https://api.dicebear.com/9.x/micah/svg?seed=Lucas&baseColor=f5cfa0&earringsProbability=0",
    desc: "Visão e velocidade. Quer resultados concretos.",
    voiceId: "ErXwobaYiN019PkySvjV",
  },
  {
    key: "Líder técnico",
    name: "Ana",
    avatarUrl: "https://api.dicebear.com/9.x/lorelei/svg?seed=Ana",
    desc: "Profundidade técnica e precisão nos detalhes.",
    voiceId: "AZnzlk1XvdvUeBnXmlld",
  },
];


function SubStepDots({ current }) {
  return (
    <div className="flex items-center gap-2 justify-center mt-1">
      {[1, 2, 3].map(s => (
        <div key={s} style={{
          height: 6,
          width: s === current ? 20 : 6,
          borderRadius: 99,
          background: s < current ? "#00DCA4" : s === current ? "#003326" : "#E5E9EA",
          transition: "all 0.25s",
        }} />
      ))}
    </div>
  );
}

export default function StepConfig({ onNext, onBack }) {
  const [subStep, setSubStep]                       = useState(1);
  const [interviewType, setInterviewType]           = useState("Triagem");
  const [interviewerProfile, setInterviewerProfile] = useState("Gestor exigente");
  const [micStatus, setMicStatus]                   = useState("idle"); // idle|granted|denied
  const [cameraStatus, setCameraStatus]             = useState("idle"); // idle|granted|denied

  const hasSpeechRecognition = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  async function requestPermissions() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach(t => t.stop());
      setMicStatus("granted");
      setCameraStatus("granted");
      return;
    } catch (_) {}
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicStatus("granted");
      setCameraStatus("denied");
    } catch {
      setMicStatus("denied");
      setCameraStatus("denied");
    }
  }

  function handleStart() {
    if (micStatus !== "granted") return;
    onNext({
      interview_type:      interviewType,
      interviewer_profile: interviewerProfile,
      camera_granted:      cameraStatus === "granted",
    });
  }

  const micGranted  = micStatus === "granted";
  const camGranted  = cameraStatus === "granted";
  const permDone    = micStatus !== "idle";
  const bothGranted = micGranted && camGranted;

  // ── Sub-step 1: Interview type ───────────────────────────────────────────────
  if (subStep === 1) {
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "#B0FCEA", color: "#00664C" }}>
            Etapa 2 de 3
          </div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Que tipo de entrevista?
          </h2>
          <p className="text-sm" style={{ color: "#646C6F" }}>Escolha o formato que melhor representa a vaga.</p>
          <SubStepDots current={1} />
        </div>

        <div className="rounded-2xl p-5 bg-white" style={{ border: "1px solid #E5E9EA" }}>
          <div className="grid grid-cols-2 gap-3">
            {INTERVIEW_TYPES.map(t => {
              const Icon = t.icon;
              const selected = interviewType === t.key;
              return (
                <button key={t.key} onClick={() => setInterviewType(t.key)}
                  className="p-4 rounded-2xl text-left transition-all flex flex-col gap-2.5"
                  style={selected
                    ? { background: "#EAFAF6", border: "2px solid #00DCA4" }
                    : { background: "#F9FAFA", border: "2px solid transparent" }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: selected ? "#00DCA4" : t.bg }}>
                    <Icon className="w-4 h-4" style={{ color: selected ? "#003326" : t.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: "#003326" }}>{t.key}</p>
                    <p className="text-xs leading-tight mt-0.5" style={{ color: "#99A6AB" }}>{t.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onBack}
            className="flex items-center gap-1 px-4 py-3 rounded-full text-sm font-semibold"
            style={{ background: "#F2F4F4", color: "#646C6F" }}>
            <ChevronLeft className="w-4 h-4" /> Voltar
          </button>
          <button onClick={() => setSubStep(2)}
            className="flex-1 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2"
            style={{ background: "#00DCA4", color: "#003326" }}>
            Próximo <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Sub-step 2: Interviewer ──────────────────────────────────────────────────
  if (subStep === 2) {
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "#B0FCEA", color: "#00664C" }}>
            Etapa 2 de 3
          </div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Escolha seu entrevistador
          </h2>
          <p className="text-sm" style={{ color: "#646C6F" }}>Cada perfil tem uma abordagem diferente.</p>
          <SubStepDots current={2} />
        </div>

        <div className="rounded-2xl p-5 bg-white" style={{ border: "1px solid #E5E9EA" }}>
          <div className="grid grid-cols-2 gap-3">
            {INTERVIEWER_PROFILES.map(p => {
              const selected = interviewerProfile === p.key;
              return (
                <button key={p.key} onClick={() => setInterviewerProfile(p.key)}
                  className="p-4 rounded-2xl text-left transition-all flex flex-col gap-2"
                  style={selected
                    ? { background: "#EAFAF6", border: "2px solid #00DCA4" }
                    : { background: "#F9FAFA", border: "2px solid transparent" }}>
                  <div className="flex items-center gap-3">
                    <img src={p.avatarUrl} alt={p.name}
                      className="w-12 h-12 rounded-full flex-shrink-0 object-cover"
                      style={{ background: "#E5E9EA", padding: "2px" }} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: "#003326" }}>{p.name}</p>
                      <p className="text-xs font-semibold truncate" style={{ color: selected ? "#00664C" : "#99A6AB" }}>{p.key}</p>
                    </div>
                  </div>
                  <p className="text-xs leading-tight" style={{ color: "#646C6F" }}>{p.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={() => setSubStep(1)}
            className="flex items-center gap-1 px-4 py-3 rounded-full text-sm font-semibold"
            style={{ background: "#F2F4F4", color: "#646C6F" }}>
            <ChevronLeft className="w-4 h-4" /> Voltar
          </button>
          <button onClick={() => setSubStep(3)}
            className="flex-1 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2"
            style={{ background: "#00DCA4", color: "#003326" }}>
            Próximo <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Sub-step 3: Permissions ─────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="text-center space-y-1">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
          style={{ background: "#B0FCEA", color: "#00664C" }}>
          Etapa 2 de 3
        </div>
        <h2 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Quase lá
        </h2>
        <p className="text-sm" style={{ color: "#646C6F" }}>Libere o microfone e a câmera para começar.</p>
        <SubStepDots current={3} />
      </div>

      {/* Browser compatibility check */}
      {!hasSpeechRecognition && (
        <div className="rounded-2xl p-5 space-y-3" style={{ background: "#FEF3C7", border: "1.5px solid #F59E0B" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FBBF24" }}>
              <AlertCircle className="w-5 h-5" style={{ color: "#78350F" }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: "#78350F" }}>Navegador não compatível</p>
              <p className="text-xs mt-0.5" style={{ color: "#92400E" }}>
                A entrevista simulada usa reconhecimento de voz, que não é suportado pelo seu navegador.
              </p>
            </div>
          </div>
          <div className="rounded-xl p-3" style={{ background: "#FFF8E1" }}>
            <p className="text-xs font-semibold mb-1" style={{ color: "#78350F" }}>Use um destes navegadores:</p>
            <ul className="text-xs space-y-0.5" style={{ color: "#92400E" }}>
              <li><strong>Google Chrome</strong> (recomendado)</li>
              <li><strong>Microsoft Edge</strong></li>
              <li><strong>Safari</strong> (macOS/iOS 14.5+)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Permissões: mic + câmera */}
      <div className="rounded-2xl p-4"
        style={{
          background: bothGranted ? "#EAFAF6" : micGranted ? "#EAFAF6" : micStatus === "denied" ? "#FADBDF" : "#F2F4F4",
          border: `1.5px solid ${bothGranted ? "#00DCA4" : micGranted ? "#00DCA4" : micStatus === "denied" ? "#EF9A9E" : "#E5E9EA"}`,
        }}>
        {!permDone ? (
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#E5E9EA" }}>
                <Mic className="w-5 h-5" style={{ color: "#646C6F" }} />
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#E5E9EA" }}>
                <Camera className="w-5 h-5" style={{ color: "#646C6F" }} />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: "#2E3233" }}>Microfone e câmera</p>
              <p className="text-xs mt-0.5" style={{ color: "#99A6AB" }}>
                Necessário para a entrevista por voz e análise visual da IA.
              </p>
            </div>
            <button onClick={requestPermissions}
              className="px-3 py-2 rounded-full text-xs font-bold flex-shrink-0"
              style={{ background: "#00DCA4", color: "#003326" }}>
              Permitir
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {/* Mic row */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: micGranted ? "#00DCA4" : "#FADBDF" }}>
                {micGranted
                  ? <CheckCircle className="w-4 h-4 text-white" />
                  : <AlertCircle className="w-4 h-4" style={{ color: "#892E38" }} />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: micGranted ? "#003326" : "#892E38" }}>
                  {micGranted ? "Microfone autorizado ✓" : "Microfone negado — necessário"}
                </p>
              </div>
            </div>
            {/* Camera row */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: camGranted ? "#00DCA4" : "#E5E9EA" }}>
                {camGranted
                  ? <Camera className="w-4 h-4 text-white" />
                  : <CameraOff className="w-4 h-4" style={{ color: "#99A6AB" }} />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: camGranted ? "#003326" : "#646C6F" }}>
                  {camGranted
                    ? "Câmera autorizada ✓ — análise visual ativada"
                    : "Câmera não disponível — análise visual desativada"}
                </p>
              </div>
            </div>
            {micStatus === "denied" && (
              <p className="text-xs" style={{ color: "#892E38" }}>
                Libere o microfone nas configurações do navegador e recarregue a página.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={() => setSubStep(2)}
          className="flex items-center gap-1 px-4 py-3 rounded-full text-sm font-semibold"
          style={{ background: "#F2F4F4", color: "#646C6F" }}>
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <button onClick={handleStart} disabled={!micGranted || !hasSpeechRecognition}
          className="flex-1 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all"
          style={micGranted && hasSpeechRecognition
            ? { background: "#00DCA4", color: "#003326" }
            : { background: "#E5E9EA", color: "#99A6AB" }}>
          Iniciar simulação <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
